import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';

const Sports = () => {
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('all');
  const [selectedSport, setSelectedSport] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/admin/api/sports.php');
        const data = await res.json();
        if (Array.isArray(data)) setSports(data);
        else if (data.data) setSports(data.data);
      } catch { /* silent */ } finally {
        setLoading(false);
      }
    })();
  }, []);

  const uniqueTypes = [...new Set(sports.map(s => s.type).filter(Boolean))];
  const uniqueSports = [...new Set(sports.map(s => s.sport).filter(Boolean))];

  const filtered = sports.filter(s => {
    const t = selectedType === 'all' || s.type === selectedType;
    const sp = selectedSport === 'all' || s.sport === selectedSport;
    return t && sp;
  });

  const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '';

  const TYPE_STYLES = {
    achievement: 'bg-amber-100 text-amber-700 border border-amber-200',
    event: 'bg-primary/10 text-primary border border-primary/20',
    default: 'bg-green-100 text-green-700 border border-green-200',
  };

  return (
    <div className="page-enter">
      <Helmet>
        <title>Sports Corner — PAK School &amp; College System | Athletics &amp; Achievements</title>
        <meta name="description" content="Discover PAK School & College System's sports programs, athletic achievements, and sporting events. We build champions on and off the field." />
        <link rel="canonical" href="https://pakschoolandcollege.com/sports" />
      </Helmet>

      <div className="page-hero">
        <div className="page-hero-content">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white/80 text-sm mb-5">
            🏆 Athletics &amp; Achievements
          </div>
          <h1>Sports <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">Corner</span></h1>
          <p>Celebrating our student athletes and building champions — in sports and in life</p>
        </div>
      </div>

      {/* Stats bar */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-3 gap-6 text-center">
            {[
              { value: '120+', label: 'Sports Events', icon: 'fa-calendar-check', color: 'text-primary' },
              { value: '15+', label: 'Different Sports', icon: 'fa-futbol', color: 'text-accent' },
              { value: '200+', label: 'Student Athletes', icon: 'fa-running', color: 'text-green-600' },
            ].map((s, i) => (
              <div key={i}>
                <i className={`fas ${s.icon} ${s.color} text-2xl mb-2 block`} />
                <div className="font-poppins font-800 text-navy text-2xl sm:text-3xl">{s.value}</div>
                <div className="text-gray-400 font-inter text-sm mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="py-14 bg-gray-50 min-h-[50vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Filters */}
          {sports.length > 0 && (
            <div className="bg-white rounded-2xl p-5 mb-8 shadow-sm border border-gray-100 grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-navy font-poppins font-600 text-sm mb-2">Filter by Type</label>
                <select value={selectedType} onChange={e => setSelectedType(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-inter focus:outline-none focus:border-primary bg-white">
                  <option value="all">All Types</option>
                  {uniqueTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-navy font-poppins font-600 text-sm mb-2">Filter by Sport</label>
                <select value={selectedSport} onChange={e => setSelectedSport(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-inter focus:outline-none focus:border-primary bg-white">
                  <option value="all">All Sports</option>
                  {uniqueSports.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          )}

          {loading && <div className="flex flex-col items-center py-24 gap-4"><div className="spinner" /><p className="text-gray-400 text-sm font-inter">Loading sports...</p></div>}

          {!loading && filtered.length === 0 && (
            <div className="text-center py-20">
              <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-5">
                <i className="fas fa-trophy text-3xl text-gray-300" />
              </div>
              <h3 className="font-poppins font-700 text-gray-700 text-xl mb-2">No Sports Events Found</h3>
              <p className="text-gray-400 font-inter text-sm">Sports events and achievements will appear here.</p>
            </div>
          )}

          {!loading && filtered.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((sport) => (
                <div key={sport.id} className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 overflow-hidden">
                  {sport.image_path && (
                    <div className="h-44 overflow-hidden">
                      <img src={`admin/${sport.image_path}`} alt={sport.event_name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className={`text-xs font-poppins font-700 px-2.5 py-1 rounded-full ${TYPE_STYLES[sport.type] || TYPE_STYLES.default}`}>
                        {sport.type}
                      </span>
                      <span className="text-xs text-gray-400 font-inter">{fmtDate(sport.date)}</span>
                    </div>
                    <h3 className="font-poppins font-700 text-navy text-base mb-2 group-hover:text-primary transition-colors">{sport.event_name}</h3>
                    <div className="space-y-1.5 mb-3">
                      {sport.sport && <div className="flex items-center gap-2 text-xs text-gray-400 font-inter"><i className="fas fa-futbol text-primary w-3.5" />{sport.sport}</div>}
                      {sport.location && <div className="flex items-center gap-2 text-xs text-gray-400 font-inter"><i className="fas fa-map-marker-alt text-accent w-3.5" />{sport.location}</div>}
                      {sport.results && <div className="flex items-center gap-2 text-xs text-amber-600 font-inter font-600"><i className="fas fa-medal w-3.5" />{sport.results}</div>}
                    </div>
                    {sport.description && <p className="text-gray-400 text-xs font-inter leading-relaxed line-clamp-3">{sport.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Sports;
